// Shared worker-type classification helpers.
//
// The engagement model dictates which employer CTA is appropriate:
//   - Day-rate eligible → "Send job offer" (structured day-rate offer form)
//   - Quote-based      → "Request quote"  (lightweight quote form → first chat message)
//
// A tradesperson is treated as day-rate eligible when they are an
// individual_tradesperson AND have NOT declared themselves as price-work,
// labour-only or supply-and-fit via `preferred_work_type`.

export const DAY_RATE_WORKER_TYPES = new Set(['individual_tradesperson']);

export const QUOTE_BASED_PREFERRED_TYPES = new Set([
  'price_work',
  'labour_only',
  'supply_and_fit',
]);

/**
 * @param {string|undefined} workerType
 * @param {string|string[]|undefined} preferredWorkType
 * @returns {boolean} true when the tradesperson should receive a day-rate job offer
 *
 * SAFETY: when worker_type is missing/undefined we default to NON day-rate
 * (returns false). This makes the system bias to the conservative
 * "Request Quote" flow rather than silently routing subcontractor / team /
 * ltd partners through the structured day-rate offer form.
 */
export function isDayRateEligible(workerType, preferredWorkType) {
  const pwtList = Array.isArray(preferredWorkType)
    ? preferredWorkType
    : preferredWorkType
    ? [preferredWorkType]
    : [];

  // Explicit override — if they accept day_rate, honour it regardless of worker_type.
  if (pwtList.includes('day_rate') || pwtList.includes('emergency_callout')) return true;

  // Any explicit quote-based preference blocks the day-rate flow.
  if (pwtList.some((p) => QUOTE_BASED_PREFERRED_TYPES.has(p))) return false;

  // Conservative: only positively-known individual_tradesperson values are day-rate.
  if (!workerType) return false;
  return DAY_RATE_WORKER_TYPES.has(workerType);
}

export function workerTypeLabel(workerType) {
  switch (workerType) {
    case 'self_employed_subcontractor':
      return 'Subcontractor';
    case 'small_trade_team_gang':
      return 'Trade Team';
    case 'limited_company_trade_contractor':
      return 'Ltd Company';
    case 'individual_tradesperson':
      return 'Sole Trader';
    default:
      return null;
  }
}
